/**
 * The Keybindings settings page.
 *
 * Owns the page's data and mutations; `KeybindingsList` renders. Replaces the
 * two "here is the path, open it in your editor" rows that were the whole
 * keybindings surface before — the file escape hatch survives as a header
 * action, because a `when` clause is still faster to write by hand.
 *
 * @module
 */
import { FileJsonIcon, PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { isElectron } from "../../env";
import { resolveAndPersistPreferredEditor } from "../../editorPreferences";
import { ensureLocalApi } from "../../localApi";
import {
  useServerAvailableEditors,
  useServerKeybindings,
  useServerKeybindingsConfigPath,
} from "../../rpc/serverState";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import {
  buildKeybindingRows,
  commandLabel,
  type KeybindingRow,
  keybindingCommandOptions,
  WHEN_VARIABLE_OPTIONS,
} from "./KeybindingsSettings.logic";
import { KeybindingsTable } from "./KeybindingsTable";
import { KeybindingTableRow } from "./KeybindingTableRow";
import { NewKeybindingTableRow } from "./NewKeybindingTableRow";
import { SettingsPageContainer, SettingsSection } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";

/**
 * Match on the rendered label, the raw command id, and the chord.
 *
 * People look for a keybinding three ways — by what it does ("terminal"), by
 * the id they saw in `keybindings.json`, and by the chord they keep hitting by
 * accident — and only the first is visible in the row's heading.
 */
export function filterKeybindingRows(
  rows: ReadonlyArray<KeybindingRow>,
  query: string,
): ReadonlyArray<KeybindingRow> {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return rows;
  return rows.filter((row) =>
    [commandLabel(row.command), row.command, row.key, row.when].some((field) =>
      field.toLowerCase().includes(needle),
    ),
  );
}

export function KeybindingsSettingsPanel() {
  return (
    <SettingsPageContainer>
      <KeybindingsSection />
    </SettingsPageContainer>
  );
}

function KeybindingsSection() {
  const resolved = useServerKeybindings();
  const [query, setQuery] = useState("");
  const [recordingRowId, setRecordingRowId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const rows = useMemo(() => buildKeybindingRows(resolved), [resolved]);
  const visibleRows = useMemo(() => filterKeybindingRows(rows, query), [rows, query]);
  const commandOptions = useMemo(() => keybindingCommandOptions(resolved), [resolved]);

  const runMutation = (operation: Promise<unknown>, failure: string) => {
    setRecordingRowId(null);
    setError(null);
    // The resolved config is republished over the `keybindingsUpdated` event,
    // so there is no local state to reconcile — only a failure to report.
    void operation.catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : failure);
    });
  };

  const handleRebind = (row: KeybindingRow, key: string) => {
    runMutation(
      ensureLocalApi().server.upsertKeybinding(
        row.when === ""
          ? { key, command: row.command }
          : { key, command: row.command, when: row.when },
      ),
      "Unable to save keybinding.",
    );
  };

  const handleReset = (row: KeybindingRow) => {
    runMutation(
      ensureLocalApi().server.removeKeybinding({ command: row.command }),
      "Unable to reset keybinding.",
    );
  };

  // Reset and Remove are the same call against a command-keyed store, and that
  // is not a duplicate to tidy away: dropping the custom rule is what restores
  // a default, and what unbinds a command that never had one. The two names are
  // the two outcomes; the row offers whichever it can honestly promise.
  const handleRemove = handleReset;

  const handleChangeWhen = (row: KeybindingRow, when: string | undefined) => {
    runMutation(
      ensureLocalApi().server.upsertKeybinding(
        when === undefined
          ? { key: row.key, command: row.command }
          : { key: row.key, command: row.command, when },
      ),
      "Unable to save when clause.",
    );
  };

  const handleAdd = (input: {
    readonly command: KeybindingRow["command"];
    readonly key: string;
    readonly when: string | undefined;
  }) => {
    setIsAdding(false);
    runMutation(
      ensureLocalApi().server.upsertKeybinding(
        input.when === undefined
          ? { key: input.key, command: input.command }
          : { key: input.key, command: input.command, when: input.when },
      ),
      "Unable to add keybinding.",
    );
  };

  return (
    <SettingsSection
      {...searchableSetting("keybindings-shortcuts")}
      headerAction={
        <div className="flex items-center gap-1.5">
          <Input
            aria-label="Search keybindings"
            className="w-44"
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder="Search keybindings"
            size="sm"
            type="search"
            value={query}
          />
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  aria-label="Add keybinding"
                  onClick={() => setIsAdding(true)}
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                />
              }
            >
              <PlusIcon className="size-3" />
            </TooltipTrigger>
            <TooltipPopup side="top">Add keybinding</TooltipPopup>
          </Tooltip>
          <OpenKeybindingsFileAction />
        </div>
      }
    >
      {error === null ? null : (
        <p className="px-3 text-destructive-foreground text-xs sm:px-4" role="alert">
          {error}
        </p>
      )}
      <KeybindingsTable isEmpty={visibleRows.length === 0 && !isAdding}>
        {isAdding ? (
          <NewKeybindingTableRow
            commandOptions={commandOptions}
            isSaving={false}
            onCancel={() => setIsAdding(false)}
            onSave={handleAdd}
            rows={rows}
            variables={WHEN_VARIABLE_OPTIONS}
          />
        ) : null}
        {visibleRows.map((row) => (
          <KeybindingTableRow
            isRecording={recordingRowId === row.id}
            isSaving={false}
            key={row.id}
            onCancelRecording={() => setRecordingRowId(null)}
            onChangeWhen={(when) => handleChangeWhen(row, when)}
            onRebind={(shortcut) => handleRebind(row, shortcut)}
            onRemove={() => handleRemove(row)}
            onReset={() => handleReset(row)}
            onStartRecording={() => setRecordingRowId(row.id)}
            row={row}
            variables={WHEN_VARIABLE_OPTIONS}
          />
        ))}
      </KeybindingsTable>
    </SettingsSection>
  );
}

/**
 * The old surface, demoted to a header action.
 *
 * Desktop-only because it shells out to an editor, which is why the search
 * catalog marks the row `desktopOnly`.
 */
function OpenKeybindingsFileAction() {
  const configPath = useServerKeybindingsConfigPath();
  const availableEditors = useServerAvailableEditors();
  const [opening, setOpening] = useState(false);

  if (!isElectron) return null;

  const openFile = () => {
    if (!configPath) return;
    const editor = resolveAndPersistPreferredEditor(availableEditors ?? []);
    if (!editor) return;
    setOpening(true);
    void ensureLocalApi()
      .shell.openInEditor(configPath, editor)
      .finally(() => setOpening(false));
  };

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size="xs"
            variant="outline"
            disabled={!configPath || opening}
            aria-label="Edit keybindings.json"
            onClick={openFile}
          >
            <FileJsonIcon className="size-3" />
            {opening ? "Opening…" : "Edit file"}
          </Button>
        }
      />
      <TooltipPopup side="top">{configPath ?? "Resolving keybindings path…"}</TooltipPopup>
    </Tooltip>
  );
}
